import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  BarChart3, Euro, Award, Rocket, PhoneCall, Users2, TrendingUp, Percent,
} from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import KpiCard from '../components/dashboard/KpiCard';
import { fmtMoney, PIPELINE_STAGES, DEAL_STAGES } from '../components/crm/constants';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TONE_MAP = {
  'text-blue-500': 'blue', 'text-emerald-500': 'emerald', 'text-violet-500': 'violet',
  'text-amber-500': 'amber', 'text-slate-500': 'slate', 'text-indigo-500': 'indigo',
};

const KPI = ({ icon, label, value, sub, tone, testId }) => (
  <KpiCard icon={icon} label={label} value={value} caption={sub} tone={TONE_MAP[tone] || 'blue'} testId={testId} />
);

const FunnelBar = ({ stages, counts, testId }) => {
  const max = Math.max(...stages.map((s) => counts[s.value] || 0), 1);
  return (
    <div className="space-y-2.5" data-testid={testId}>
      {stages.map((s) => (
        <div key={s.value} className="flex items-center gap-3" data-testid={`${testId}-${s.value}`}>
          <span className="text-xs text-slate-500 w-24 shrink-0">{s.label}</span>
          <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${s.bar || s.dot} transition-all duration-500`}
              style={{ width: `${Math.max(Math.round(((counts[s.value] || 0) / max) * 100), (counts[s.value] || 0) > 0 ? 6 : 0)}%` }}
            />
          </div>
          <span className="text-sm font-semibold text-slate-700 w-8 text-right tabular-nums">{counts[s.value] || 0}</span>
        </div>
      ))}
    </div>
  );
};

export default function SalesReports() {
  const { userProfile } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);

  const load = useCallback(async () => {
    const res = await crmFetch('/reports');
    if (res.tableMissing) setTableMissing(true);
    else if (res.success && res.data) { setData(res.data); setTableMissing(false); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  const isTeam = data?.scope === 'team';
  const monthly = data?.monthlyRevenue || [];
  const maxMonthly = Math.max(...monthly, 1);
  const hasMonthly = monthly.some((m) => m > 0);

  return (
    <SalesLayout>
      <div data-testid="reports-page">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" /> {isTeam ? 'Team Reports' : 'My Reports'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isTeam ? 'Leadership view across all sales executives.' : 'Your recruitment and software performance this year.'}
          </p>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              CRM tables not migrated yet — run the CRM migrations in the Supabase SQL Editor, then refresh.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPI icon={Euro} label="Revenue (YTD)" tone="text-blue-500"
                value={loading ? '…' : (fmtMoney(t?.revenueYtd ?? 0) || '€0')}
                sub={`${t?.placementsYtd ?? 0} placements`} testId="reports-kpi-revenue" />
              <KPI icon={Award} label="Commission Earned" tone="text-emerald-500"
                value={loading ? '…' : (fmtMoney(t?.commissionEarnedYtd ?? 0) || '€0')}
                sub={`${fmtMoney(t?.commissionPending ?? 0) || '€0'} pending`} testId="reports-kpi-commission" />
              <KPI icon={Rocket} label="Software Won (ARR)" tone="text-violet-500"
                value={loading ? '…' : (fmtMoney(t?.softwareWonArrYtd ?? 0) || '€0')}
                sub={`${t?.softwareWinRate ?? 0}% win rate`} testId="reports-kpi-software" />
              <KPI icon={PhoneCall} label="Activities (Month)" tone="text-amber-500"
                value={loading ? '…' : (t?.activitiesThisMonth?.total ?? 0)}
                sub={`${t?.activitiesThisMonth?.calls ?? 0} calls · ${t?.activitiesThisMonth?.meetings ?? 0} meetings`} testId="reports-kpi-activities" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPI icon={Users2} label="Accounts" tone="text-slate-500" value={loading ? '…' : (t?.accounts ?? 0)} testId="reports-kpi-accounts" />
              <KPI icon={TrendingUp} label="Open Jobs" tone="text-blue-500" value={loading ? '…' : (t?.openJobs ?? 0)} testId="reports-kpi-jobs" />
              <KPI icon={Users2} label="Active Candidates" tone="text-indigo-500" value={loading ? '…' : (t?.activeCandidates ?? 0)} testId="reports-kpi-candidates" />
              <KPI icon={Percent} label="Software Open (ARR)" tone="text-violet-500" value={loading ? '…' : (fmtMoney(t?.softwareOpenArr ?? 0) || '€0')} testId="reports-kpi-software-open" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card data-testid="reports-recruitment-funnel">
                <CardHeader className="pb-3"><CardTitle className="text-base">Recruitment Pipeline</CardTitle></CardHeader>
                <CardContent>
                  <FunnelBar stages={PIPELINE_STAGES.filter((s) => s.value !== 'rejected')} counts={data?.recruitmentFunnel || {}} testId="recruitment-funnel" />
                </CardContent>
              </Card>
              <Card data-testid="reports-software-funnel">
                <CardHeader className="pb-3"><CardTitle className="text-base">Software Pipeline</CardTitle></CardHeader>
                <CardContent>
                  <FunnelBar stages={DEAL_STAGES} counts={data?.softwareFunnel || {}} testId="software-funnel" />
                </CardContent>
              </Card>
            </div>

            <Card className="mb-6" data-testid="reports-monthly-revenue">
              <CardHeader className="pb-3"><CardTitle className="text-base">Monthly Placement Revenue ({new Date().getFullYear()})</CardTitle></CardHeader>
              <CardContent>
                {!hasMonthly && <p className="text-sm text-gray-500 py-6 text-center">No placement revenue booked yet this year.</p>}
                {hasMonthly && (
                  <div className="flex items-end gap-2 h-40">
                    {monthly.map((v, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 group" data-testid={`revenue-month-${i}`}>
                        <div className="w-full rounded-t-lg transition-all duration-500 bg-gradient-to-t from-blue-600 to-blue-400 group-hover:from-blue-700 group-hover:to-blue-500" style={{ height: `${Math.round((v / maxMonthly) * 100)}%`, minHeight: v > 0 ? '6px' : '0' }} title={fmtMoney(v) || '€0'} />
                        <span className="text-[10px] text-slate-400">{MONTHS[i]}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {isTeam && (
              <Card data-testid="reports-leaderboard">
                <CardHeader className="pb-3"><CardTitle className="text-base">Sales Executive Leaderboard</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50/60 text-left text-xs text-gray-500 uppercase tracking-wide">
                          <th className="px-4 py-3 font-medium">Executive</th>
                          <th className="px-4 py-3 font-medium text-center">Accounts</th>
                          <th className="px-4 py-3 font-medium text-center">Open Jobs</th>
                          <th className="px-4 py-3 font-medium text-center">Placements</th>
                          <th className="px-4 py-3 font-medium text-right">Revenue</th>
                          <th className="px-4 py-3 font-medium text-right">Commission</th>
                          <th className="px-4 py-3 font-medium text-center">Deals Won</th>
                          <th className="px-4 py-3 font-medium text-right">Software ARR</th>
                          <th className="px-4 py-3 font-medium text-center">Activities</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
                        {!loading && (data?.perExec || []).length === 0 && (
                          <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">No sales executives found.</td></tr>
                        )}
                        {(data?.perExec || []).map((e) => (
                          <tr key={e.userId} className="border-b last:border-0 hover:bg-gray-50/60" data-testid={`leaderboard-row-${e.userId}`}>
                            <td className="px-4 py-2.5 font-medium text-gray-900">{e.name}</td>
                            <td className="px-4 py-2.5 text-center text-gray-600">{e.accounts}</td>
                            <td className="px-4 py-2.5 text-center text-gray-600">{e.openJobs}</td>
                            <td className="px-4 py-2.5 text-center text-gray-600">{e.placementsYtd}</td>
                            <td className="px-4 py-2.5 text-right text-gray-900">{fmtMoney(e.revenueYtd) || '€0'}</td>
                            <td className="px-4 py-2.5 text-right text-emerald-600 font-medium">{fmtMoney(e.commissionEarned) || '€0'}</td>
                            <td className="px-4 py-2.5 text-center text-gray-600">{e.dealsWon}</td>
                            <td className="px-4 py-2.5 text-right text-violet-600">{fmtMoney(e.softwareArr) || '€0'}</td>
                            <td className="px-4 py-2.5 text-center text-gray-600">{e.activitiesTotal}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </SalesLayout>
  );
}
